//@ts-nocheck

//Components
import ShellLayout from '../../layouts/ShellLayout/ShellLayout';
import shellHeaderBuilder from '../../utils/shellHeaderBuilder';
import TripHeader from '../../components/Trip/TripHeader/TripHeader';
import TripHeaderActions from '../../components/Trip/TripHeaderActions/TripHeaderActions';
import CustomTabs from '../../components/Tabs/CustomTabs';
import CustomTab from '../../components/Tabs/CustomTab';
import TripImportSummaryPanel from '../../components/Trip/TripImportSummaryPanel/TripImportSummaryPanel';
import ItinerarySkeleton from '../../components/Skeleton/ItinerarySkeleton/ItinerarySkeleton';
import { Suspense, lazy, useEffect, useState } from 'react';
import { getTenant } from '../../services/tenant/tenant';

// Lazy load all major components
const Itinerary = lazy(
  () => import('../../components/Trip/Itinerary/Itinerary')
);
const InfoDocs = lazy(() => import('../../components/Trip/InfoDocs/InfoDocs'));
const TripPricing = lazy(
  () => import('../../components/Trip/Pricing/TripPricing/TripPricing')
);
const BookingsPanel = lazy(
  () => import('../../components/Trip/Bookings/BookingsPanel/BookingsPanel')
);
const PassengersWorkspace = lazy(
  () =>
    import('../../components/Trip/Passengers/PassengersWorkspace/PassengersWorkspace')
);
const CustomizeSettings = lazy(
  () => import('../../components/Trip/CustomizeSettings/CustomizeSettings')
);
const PricingPanel = lazy(
  () => import('../../components/Trip/Pricing/PrincingPanel/PrincingPanel')
);
const PassengersPanel = lazy(
  () =>
    import('../../components/Trip/Passengers/PassengersPanel/PassengersPanel')
);
const TripOverview = lazy(
  () => import('../../components/Trip/TripOverview/TripOverview')
);

//Redux Slices
import {
  createTrip,
  refreshTripDetailedDays
} from '../../redux/slices/trip/trip';
import { updateServices } from '../../redux/slices/services/services';
import { updateStatuses } from '../../redux/slices/tripstatuses/tripstatuses';
import { updateDestinations } from '../../redux/slices/destinations/destinations';
import { addShouldLibraryClose } from '../../redux/slices/library/library';

//Services
import { getTrip, getTripDetailedDays } from '../../services/trip/trip';
import { getDestinations } from '../../services/destination/destination';
import { getServiceTypes } from '../../services/servicetype/servicetype';
import { getTripStatuses } from '../../services/tripstatus/tripstatus';
import { getContentTypes } from '../../services/contenttype/contenttype';
import { updateContentTypes } from '../../redux/slices/contenttypes/contenttypes';
import { updateTenant } from '../../redux/slices/tenant/tenant';

// Other dependencies
import {
  useLoaderData,
  useLocation,
  useNavigate,
  useSearchParams
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { XGTabs } from 'xg-cl';
import { useDispatch } from 'react-redux';
import * as PropTypes from 'prop-types';
import './Trip.css';

// Maps the `?tab=` deep link (used by the trip import "Here's what I did"
// summary) to the matching index in the tripNavigationEnabled tab array
// below: Overview, Itinerary, Info & Docs, Pricing, ...
const TAB_PARAM_TO_INDEX = {
  overview: 0,
  itinerary: 1,
  infoDocs: 2,
  pricing: 3
};

/**
 * Wraps tab content with Suspense and loading fallback
 * @param {React.ReactNode} children - Tab content
 * @param {boolean} [loading=false] - Loading state
 * @returns {React.ReactElement} Wrapped content
 */
const TabContentWrapper = ({ children, loading = false }) => (
  <Suspense fallback={loading ? <ItinerarySkeleton /> : <></>}>
    {children}
  </Suspense>
);

TabContentWrapper.propTypes = {
  children: PropTypes.node,
  loading: PropTypes.bool
};

/**
 * Renders trip details page with tabs for different views (itinerary, pricing, etc)
 * @param {Object} props - Component props
 * @param {string} props.id - Trip component ID
 * @returns {JSX.Element} Trip page component
 */
const Trip = ({ id }) => {
  const { tripId } = useLoaderData();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation(['trip', 'common', 'trips']);
  const [isTripLoading, setIsTripLoading] = useState(true);
  const [didTripFetchSucceed, setDidTripFetchSucceed] = useState(true);
  const { tripNavigation, enableDisplayStatuses, enablePassengerWorkingSpace } =
    useFlags();
  const passengerWorkspaceEnabled =
    enablePassengerWorkingSpace ||
    (import.meta.env.DEV &&
      import.meta.env.VITE_ENABLE_PASSENGER_WORKING_SPACE === 'true');
  const tripNavigationEnabled = tripNavigation || passengerWorkspaceEnabled;

  // Redirect to trips list if fetch failed - must be in useEffect to prevent infinite loop
  useEffect(() => {
    if (!didTripFetchSucceed) {
      navigate('/trips');
    }
  }, [didTripFetchSucceed, navigate]);

  const tripTabs = tripNavigationEnabled
    ? [
        {
          title: t('overview'),
          content: (
            <TabContentWrapper loading={isTripLoading}>
              {<TripOverview id='trip-overview' />}
            </TabContentWrapper>
          )
        },
        {
          title: t('itinerary'),
          content: (
            <TabContentWrapper loading={isTripLoading}>
              {!isTripLoading && <Itinerary id='trip-itinerary' />}
            </TabContentWrapper>
          )
        },
        {
          title: t('infoAndDocs'),
          content: (
            <TabContentWrapper>
              <InfoDocs id='trip-info-and-docs' />
            </TabContentWrapper>
          )
        },
        {
          title: t('pricing'),
          content: (
            <TabContentWrapper>
              <TripPricing id='trip-pricing-page' tripId={Number(tripId)} />
            </TabContentWrapper>
          )
        },
        {
          title: t('bookings'),
          content: (
            <TabContentWrapper>
              <BookingsPanel key={tripId} />
            </TabContentWrapper>
          )
        },
        ...(passengerWorkspaceEnabled
          ? [
              {
                title: t('passengers'),
                content: (
                  <TabContentWrapper>
                    <PassengersWorkspace />
                  </TabContentWrapper>
                )
              }
            ]
          : []),
        {
          title: t('customizedSettings'),
          content: (
            <TabContentWrapper>
              <CustomizeSettings id='trip-customize-settings' />
            </TabContentWrapper>
          )
        }
      ]
    : [
        {
          title: t('itinerary'),
          content: (
            <TabContentWrapper loading={isTripLoading}>
              {!isTripLoading && <Itinerary id='trip-itinerary' />}
            </TabContentWrapper>
          )
        },
        {
          title: t('pricing'),
          content: (
            <TabContentWrapper>
              <PricingPanel id='trip-pricing-panel' />
            </TabContentWrapper>
          )
        },
        {
          title: t('passengers'),
          content: (
            <TabContentWrapper>
              <PassengersPanel />
            </TabContentWrapper>
          )
        },
        {
          title: t('bookings'),
          content: (
            <TabContentWrapper>
              <BookingsPanel key={tripId} />
            </TabContentWrapper>
          )
        }
      ];

  const refreshTenantData = async () => {
    try {
      const response = await getTenant();
      if (response?.data) {
        const {
          basecurrency,
          sellingcurrency,
          tenantdomain,
          tenantname,
          tenantlogo,
          tenantid
        } = response.data;

        const tenantData = {
          domain: tenantdomain,
          name: tenantname,
          logo: tenantlogo,
          tenantid: tenantid,
          basecurrency,
          sellingcurrency
        };

        localStorage.setItem('tenantData', JSON.stringify(tenantData));

        dispatch(updateTenant(tenantData));
      }
    } catch (error) {
      console.error('Failed to refresh tenant data:', error);
    }
  };

  const handleTabChange = async () => {
    dispatch(addShouldLibraryClose({ shouldLibraryClose: true }));

    // Refresh tenant data when main tabs change
    await refreshTenantData();
  };

  const getTopNavigation = () => {
    return (
      tripNavigationEnabled && (
        <div className='trip-top-navigation'>
          <CustomTabs
            handleTabChange={handleTabChange}
            className='header-nav-tabs'
            activeIndex={
              searchParams.get('managebooking')
                ? 4
                : (TAB_PARAM_TO_INDEX[searchParams.get('tab')] ?? 1)
            }
            // An in-page link to `?tab=` (the reopened import summary) switches the tab; it is a no-op otherwise.
            requestedIndex={TAB_PARAM_TO_INDEX[searchParams.get('tab')]}
            requestKey={location.key}
          >
            {tripTabs?.map((tab, index) => (
              <CustomTab key={index} label={tab.title}>
                {tab?.content}
              </CustomTab>
            ))}
          </CustomTabs>
        </div>
      )
    );
  };

  const mainHeader = shellHeaderBuilder({
    title: <TripHeader id='trip-header' />,
    cta: <TripHeaderActions id='trip-header-actions' />,
    navigation: getTopNavigation()
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const promises = [
          getTrip(tripId),
          getDestinations(),
          getServiceTypes(),
          getTripStatuses(),
          getContentTypes(),
          getTripDetailedDays(tripId)
        ];

        const results = await Promise.allSettled(promises);

        const [
          tripResult,
          destinationsResult,
          servicesResult,
          tripStatusesResult,
          libraryContentTypesResult,
          tripDetailedDaysResult
        ] = results;

        if (
          tripResult.status === 'fulfilled' &&
          Object.keys(tripResult?.value?.data).length > 0
        ) {
          const tripdays = tripResult.value?.data?.tripday ?? [];
          const tripcontent = tripResult.value?.data?.tripcontent ?? [];

          if (Array.isArray(tripdays)) {
            const newTripdays = tripdays.map((day, index) => ({
              ...day,
              isSelected: index === 0
            }));
            const newTripcontent = tripcontent.map((item, index) => ({
              ...item,
              isSelected: index === 0
            }));

            tripResult.value.data.tripday = newTripdays;
            tripResult.value.data.tripcontent = newTripcontent;

            const resolvedDetailedDays =
              tripDetailedDaysResult?.status === 'fulfilled' &&
              Array.isArray(tripDetailedDaysResult.value?.data)
                ? tripDetailedDaysResult.value?.data
                : null;

            const existingDetailedDays = Array.isArray(
              tripResult.value?.data?.tripdetailedday
            )
              ? tripResult.value.data.tripdetailedday
              : [];

            const tripDetailedDays =
              resolvedDetailedDays && resolvedDetailedDays?.length > 0
                ? resolvedDetailedDays
                : existingDetailedDays;

            tripResult.value.data.tripdetailedday = tripDetailedDays;

            dispatch(createTrip(tripResult.value.data));
            dispatch(
              refreshTripDetailedDays({
                tripdetailedday: tripDetailedDays
              })
            );

            // Update page title
            const tripName = tripResult.value.data?.tripname;
            if (tripName) {
              document.title = `Nivo - ${tripName}`;
            }
          } else {
            setDidTripFetchSucceed(false);
          }
        } else {
          setDidTripFetchSucceed(false);
        }

        if (destinationsResult.status === 'fulfilled') {
          dispatch(updateDestinations(destinationsResult?.value?.data || []));
        }

        if (servicesResult.status === 'fulfilled') {
          dispatch(updateServices(servicesResult?.value?.data || []));
        }

        if (tripStatusesResult.status === 'fulfilled') {
          dispatch(
            updateStatuses(
              enableDisplayStatuses
                ? [
                    ...(tripStatusesResult?.value?.data || []).filter(
                      (status) =>
                        !['TRAVELING', 'COMPLETE'].includes(
                          status?.tripstatuscode?.toUpperCase()
                        )
                    ),
                    {
                      tripstatusid: -1,
                      tripstatusname: t('trips:statusTraveling'),
                      tripstatuscode: 'TRAVELING'
                    },
                    {
                      tripstatusid: -2,
                      tripstatusname: t('trips:statusComplete'),
                      tripstatuscode: 'COMPLETE'
                    }
                  ]
                : tripStatusesResult?.value?.data || []
            )
          );
        }

        if (libraryContentTypesResult.status === 'fulfilled') {
          dispatch(
            updateContentTypes(libraryContentTypesResult?.value?.data || [])
          );
        }
      } catch (error) {
        setDidTripFetchSucceed(false);
      } finally {
        setIsTripLoading(false);
      }
    };

    fetchData();
  }, [tripId, dispatch, t, enableDisplayStatuses]);

  return (
    <div id={id} data-testid='trip-page'>
      <ShellLayout
        id='main-trip-header'
        data-testid='trip-shell'
        headerData={mainHeader}
        isLoadingContent={isTripLoading}
      >
        {!isTripLoading ? (
          !tripNavigationEnabled ? (
            <div className='trip-wrapper'>
              {/* The Overview tab (where the panel lives with the new navigation) does not exist in this layout, and its
                  ?tab= links have no tab to open, so the panel sits above the tabs without links. */}
              <TripImportSummaryPanel
                tripId={Number(tripId)}
                showLinks={false}
              />
              <XGTabs
                id='trip-tabs'
                tabs={tripTabs}
                className='trip-tab-panel'
              />
            </div>
          ) : null
        ) : (
          <ItinerarySkeleton id='itinerary-skeleton' />
        )}
      </ShellLayout>
    </div>
  );
};

Trip.propTypes = {
  id: PropTypes.string.isRequired
};

export default Trip;
